import { useAuth } from "@/contexts/AuthContext";

const PLATFORM_ROLES = ["relay_platform_admin", "super_admin"] as const;

/**
 * Client-side mirror of the SQL function `is_relay_platform_admin_from_jwt`
 * (`supabase/migrations/20260526234813_relay_platform_admin_role.sql`).
 *
 * Returns whether the currently-authenticated user holds either
 * `relay_platform_admin` or `super_admin` in their JWT `app_metadata` —
 * an array under `roles` or a single value under `role`. Both fields are
 * server-set (not user-editable), so reading them on the client is safe
 * for UX gating. RLS using the same SQL predicate enforces the real
 * answer on every query.
 */
export function useIsRelayPlatformAdmin(): {
  isPlatformAdmin: boolean;
  isLoading: boolean;
} {
  const { session, isLoading } = useAuth();

  if (isLoading) {
    return { isPlatformAdmin: false, isLoading: true };
  }
  if (!session) {
    return { isPlatformAdmin: false, isLoading: false };
  }

  const meta = (session.user.app_metadata ?? {}) as {
    roles?: unknown;
    role?: unknown;
  };

  const rolesArr = Array.isArray(meta.roles)
    ? meta.roles.filter((r): r is string => typeof r === "string")
    : [];
  const singleRole = typeof meta.role === "string" ? meta.role : null;

  const platformRoles = PLATFORM_ROLES as readonly string[];
  const isPlatformAdmin =
    rolesArr.some((r) => platformRoles.includes(r)) ||
    (singleRole !== null && platformRoles.includes(singleRole));

  return { isPlatformAdmin, isLoading: false };
}
